/**
 * One connected application, and everything staff do to it
 * (docs/44_CONNECTED_APPS.md §1).
 *
 * The header carries its state and the step that state leads to: a pending
 * application is approved (until then the server refuses it everywhere); an
 * approved one paused (a hold: nothing revoked, nobody told) or suspended
 * (every connection stopped and told); either resumed. Editing, new secrets and
 * removing are under More. The sections hold who it is, what it may do,
 * where it hears its events, and which clients connected it.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, ChevronDown, Info, KeyRound, Link2, Pencil, ShieldCheck, Trash2, Webhook } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  DropdownMenu,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  ScrollArea,
  Sections,
  StatusChip,
  api,
  formatDateTime,
  formatNumber,
  formatRelative,
  toast,
  useCan,
} from '@connect/web-shared';

import { AppDialog, SecretsDialog } from './connected-app-dialogs';
import {
  APPS,
  REVOKED_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  appKeys,
  type ConnectedApp,
  type ScopeDef,
  type Secrets,
  type StaffConnection,
} from './connected-apps';

interface Asked {
  path: string;
  title: string;
  message: string;
  label: string;
}

const DONE: Record<string, string> = {
  approve: 'Application approved',
  pause: 'Application paused',
  suspend: 'Application suspended',
  resume: 'Application resumed',
};

export function ConnectedAppDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.connected_app.edit');
  const canDelete = useCan('messaging.connected_app.delete');
  const app = useQuery({
    queryKey: appKeys.detail(publicId),
    queryFn: () => api.get<ConnectedApp>(`${APPS}/${publicId}`),
  });
  const scopes = useQuery({
    queryKey: appKeys.scopes,
    queryFn: () => api.get<ScopeDef[]>(`${APPS}/scopes`),
    staleTime: 10 * 60_000,
  });
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState<Asked | null>(null);
  const [shown, setShown] = useState<Secrets | null>(null);

  async function run(path: string) {
    try {
      if (path === 'delete') {
        await api.delete(`${APPS}/${publicId}`);
        void queryClient.invalidateQueries({ queryKey: appKeys.all });
        toast.success('Application removed');
        navigate('/messaging/connected-apps');
        return;
      }
      const answer = await api.post<ConnectedApp | Secrets>(
        `${APPS}/${publicId}/${path}`,
        path.startsWith('rotate') ? { overlap_hours: 24 } : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: appKeys.all });
      if (path.startsWith('rotate')) setShown(answer as Secrets);
      else toast.success(DONE[path] ?? 'Done');
    } catch (caught) {
      toast.error('Could not do that', caught instanceof ApiError ? caught.message : 'Try again.');
    }
  }

  if (app.isLoading) return <LoadingState label="Opening the application" />;
  if (app.isError || !app.data) {
    const error = app.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Application not found' : undefined}
        message={error instanceof ApiError ? error.message : undefined}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void app.refetch()}
      />
    );
  }
  const row = app.data;
  const labels = Object.fromEntries((scopes.data ?? []).map((s) => [s.name, s.label]));

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            {row.name}
            {row.verified ? <BadgeCheck className="size-5 text-accent" aria-label="Verified" /> : null}
          </span>
        }
        description={row.description || row.homepage_url}
        breadcrumbs={[{ label: 'Connected apps', to: '/messaging/connected-apps' }, { label: row.name }]}
        actions={
          <>
            <StatusChip value={row.status} tones={STATUS_TONES} labels={STATUS_LABELS} />
            {canEdit && row.status === 'pending' ? (
              <Button
                size="sm"
                onClick={() =>
                  setAsking({
                    path: 'approve',
                    title: `Approve ${row.name}?`,
                    message: 'Clients will be able to connect it to their accounts, within what it may ask for.',
                    label: 'Approve',
                  })
                }
              >
                Approve
              </Button>
            ) : null}
            {canEdit && row.status === 'active' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setAsking({
                    path: 'pause',
                    title: `Pause ${row.name}?`,
                    message:
                      'A hold for now: it cannot act on any account and nobody can connect it, but nothing is revoked and it is not told it was disconnected. Resume it to carry on.',
                    label: 'Pause',
                  })
                }
              >
                Pause
              </Button>
            ) : null}
            {canEdit && (row.status === 'active' || row.status === 'paused') ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setAsking({
                    path: 'suspend',
                    title: `Suspend ${row.name}?`,
                    message: 'Every connection to it stops working at once, and it is told. Resuming starts them again.',
                    label: 'Suspend',
                  })
                }
              >
                Suspend
              </Button>
            ) : null}
            {canEdit && (row.status === 'suspended' || row.status === 'paused') ? (
              <Button size="sm" variant="secondary" onClick={() => void run('resume')}>
                Resume
              </Button>
            ) : null}
            {canEdit || canDelete ? (
              <DropdownMenu
                label="More"
                trigger={(props) => (
                  <Button variant="secondary" size="sm" {...props}>
                    More
                    <ChevronDown className="size-3.5" />
                  </Button>
                )}
                items={[
                  ...(canEdit
                    ? [
                        { label: 'Edit', icon: <Pencil className="size-3.5" />, onSelect: () => setEditing(true) },
                        {
                          label: 'New client secret',
                          icon: <KeyRound className="size-3.5" />,
                          onSelect: () =>
                            setAsking({
                              path: 'rotate-secret',
                              title: 'Make a new client secret?',
                              message: 'The current one keeps working for 24 hours, so the application can be changed first.',
                              label: 'Make a new secret',
                            }),
                        },
                        ...(row.webhook_url
                          ? [
                              {
                                label: 'New webhook secret',
                                icon: <Webhook className="size-3.5" />,
                                onSelect: () =>
                                  setAsking({
                                    path: 'rotate-webhook-secret',
                                    title: 'Make a new webhook signing secret?',
                                    message:
                                      'Events are signed with both for 24 hours, so the application can be changed first.',
                                    label: 'Make a new secret',
                                  }),
                              },
                            ]
                          : []),
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        {
                          label: 'Remove',
                          icon: <Trash2 className="size-3.5" />,
                          destructive: true,
                          separated: canEdit,
                          onSelect: () =>
                            setAsking({
                              path: 'delete',
                              title: `Remove ${row.name}?`,
                              message: 'Every connection to it ends and is told. This cannot be undone.',
                              label: 'Remove',
                            }),
                        },
                      ]
                    : []),
                ]}
              />
            ) : null}
          </>
        }
      />

      {row.status === 'pending' ? (
        <Alert tone="warning" title="Waiting for approval">
          Clients cannot connect it, and it cannot get a token, until it is approved.
        </Alert>
      ) : row.status === 'paused' ? (
        <Alert tone="warning" title="Paused">
          It cannot act on any account and nobody can connect it, for now. Its connections stand, it keeps hearing its
          events, and it was not told anything: resume it to carry on.
        </Alert>
      ) : row.status === 'suspended' ? (
        <Alert tone="danger" title="Suspended">
          Every connection to it is stopped. Clients see it as suspended on their Connected apps page.
        </Alert>
      ) : null}

      <Sections
        label="Application"
        items={[
          {
            id: 'overview',
            label: 'Overview',
            hint: 'Who it is',
            icon: <Info />,
            content: (
              <Card>
                <CardHeader title="Overview" />
                <CardBody>
                  <DetailGrid>
                    <DetailField label="Client ID">
                      <code className="font-mono text-xs">{row.client_id}</code>
                    </DetailField>
                    <DetailField label="Connections">{formatNumber(row.connections)}</DetailField>
                    <DetailField label="Homepage">
                      <a className="break-all text-accent hover:underline" href={row.homepage_url} target="_blank" rel="noreferrer">
                        {row.homepage_url}
                      </a>
                    </DetailField>
                    <DetailField label="Privacy policy">
                      <span className="break-all">{row.privacy_url ?? '—'}</span>
                    </DetailField>
                    <DetailField label="Terms">
                      <span className="break-all">{row.terms_url ?? '—'}</span>
                    </DetailField>
                    <DetailField label="Verified">{row.verified ? 'Yes' : 'No'}</DetailField>
                    <DetailField label="Listed">{row.listed ? 'Yes' : 'No'}</DetailField>
                    <DetailField label="Registered">{formatDateTime(row.created_at)}</DetailField>
                    <DetailField label="Approved">{row.approved_at ? formatDateTime(row.approved_at) : '—'}</DetailField>
                    {row.paused_at ? <DetailField label="Paused">{formatDateTime(row.paused_at)}</DetailField> : null}
                    {row.suspended_at ? (
                      <DetailField label="Suspended">{formatDateTime(row.suspended_at)}</DetailField>
                    ) : null}
                  </DetailGrid>
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'access',
            label: 'Sign-in and access',
            hint: 'Redirects, what it may ask for',
            icon: <ShieldCheck />,
            content: (
              <Card>
                <CardHeader
                  title="Sign-in and access"
                  description="A client is only ever sent back to one of these addresses, compared exactly, and gives only what its own account can use."
                />
                <CardBody>
                  <DetailGrid>
                    <DetailField label="Redirect addresses">
                      <ul className="space-y-0.5 font-mono text-xs">
                        {row.redirect_uris.map((uri) => (
                          <li key={uri} className="break-all">
                            {uri}
                          </li>
                        ))}
                      </ul>
                    </DetailField>
                    <DetailField label="May ask for">
                      <ul className="space-y-0.5 text-sm">
                        {row.scopes.map((name) => (
                          <li key={name}>
                            {labels[name] ?? name} <span className="font-mono text-2xs text-faint">{name}</span>
                          </li>
                        ))}
                      </ul>
                    </DetailField>
                  </DetailGrid>
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'events',
            label: 'Events and secrets',
            hint: 'Webhook address, rotation',
            icon: <Webhook />,
            content: (
              <Card>
                <CardHeader
                  title="Events and secrets"
                  description="Its secrets are never shown again: make a new one, and the old one keeps working for 24 hours."
                />
                <CardBody>
                  <DetailGrid>
                    <DetailField label="Webhook address">
                      <span className="break-all font-mono text-xs">{row.webhook_url ?? '—'}</span>
                    </DetailField>
                    <DetailField label="Previous client secret works until">
                      {row.previous_secret_expires_at ? formatDateTime(row.previous_secret_expires_at) : '—'}
                    </DetailField>
                  </DetailGrid>
                </CardBody>
              </Card>
            ),
          },
          {
            id: 'connections',
            label: 'Connections',
            hint: 'Which clients connected it',
            icon: <Link2 />,
            content: <ConnectionsCard publicId={publicId} labels={labels} />,
          },
        ]}
      />

      <Confirm
        open={asking !== null}
        onClose={() => setAsking(null)}
        title={asking?.title ?? ''}
        message={asking?.message ?? ''}
        confirmLabel={asking?.label}
        variant={asking?.path === 'approve' || asking?.path === 'pause' || asking?.path.startsWith('rotate') ? 'primary' : 'danger'}
        onConfirm={async () => {
          if (asking) await run(asking.path);
        }}
      />
      {editing ? (
        <AppDialog
          app={row}
          scopes={scopes.data ?? []}
          onClose={() => setEditing(false)}
          onSecrets={(_, secrets) => setShown(secrets)}
        />
      ) : null}
      {shown ? <SecretsDialog name={row.name} secrets={shown} onClose={() => setShown(null)} /> : null}
    </div>
  );
}

function ConnectionsCard({ publicId, labels }: { publicId: string; labels: Record<string, string> }) {
  const connections = useQuery({
    queryKey: appKeys.connections(publicId),
    queryFn: () => api.get<StaffConnection[]>(`${APPS}/${publicId}/connections`),
  });
  return (
    <Card>
      <CardHeader title="Connections" description="Which clients connected it, and how each connection stands." />
      <CardBody className="p-0">
        {connections.data === undefined ? (
          connections.isError ? (
            <ErrorState onRetry={() => void connections.refetch()} />
          ) : (
            <LoadingState label="Loading the connections" />
          )
        ) : connections.data.length === 0 ? (
          <EmptyState title="Not connected yet" description="No client has connected it." />
        ) : (
          <ScrollArea>
            <table className="w-full min-w-[44rem] text-left text-sm">
              <thead className="border-b border-line text-2xs uppercase tracking-wide text-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">May</th>
                  <th className="px-4 py-2 font-medium">Connected</th>
                  <th className="px-4 py-2 font-medium">Last used</th>
                  <th className="px-4 py-2 font-medium">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {connections.data.map((c) => (
                  <tr key={c.public_id}>
                    <td className="px-4 py-2.5 font-medium text-heading">{c.client_name}</td>
                    <td className="px-4 py-2.5 text-xs text-body">
                      {c.scopes.map((name) => labels[name] ?? name).join(', ')}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted">
                      {formatDateTime(c.connected_at)}
                      <span className="block">by {c.connected_by}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted">
                      {c.last_used_at ? formatRelative(c.last_used_at) : 'Not yet'}
                    </td>
                    <td className="px-4 py-2.5">
                      {c.revoked_at ? (
                        <StatusChip
                          value="ended"
                          tones={{ ended: 'neutral' }}
                          labels={{ ended: REVOKED_LABELS[c.revoked_reason ?? ''] ?? 'Ended' }}
                        />
                      ) : (
                        <StatusChip value="live" tones={{ live: 'success' }} labels={{ live: 'Connected' }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </CardBody>
    </Card>
  );
}
